import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from 'shared/contexts/AuthContext';
import { UserRole } from 'shared/types';
import { APPROVER_ROLES } from 'shared/utils/roleUtils';
import { getDefaultRouteForRole } from 'shared/utils/roleRedirects';

interface RestrictedRouteProps {
  /** Let a provider admin who manages this company's work documents through,
   *  even though their role is in `blockedRoles`. */
  allowWorkDocumentManagers?: boolean;
  children: React.ReactElement;
  /** Roles that are NOT allowed to access this route */
  blockedRoles?: readonly UserRole[];
  /** Where to redirect blocked users (defaults to role-based default route) */
  redirectTo?: string;
}

/**
 * RestrictedRoute component
 *
 * Blocks specific roles from accessing certain routes.
 * If the user's role is in blockedRoles, they are redirected.
 */
export const RestrictedRoute: React.FC<RestrictedRouteProps> = ({
  children,
  blockedRoles = APPROVER_ROLES,
  redirectTo,
  allowWorkDocumentManagers = false,
}) => {
  const { user, hasRole } = useAuth();

  if (!user?.role) {
    return <Navigate to="/login" replace />;
  }

  // Un admin de proveedora prestado a un cliente entra a la Biblioteca de ese
  // cliente aunque su rol ahí sea client_staff: gestiona sus documentos de
  // trabajo por capacidad, no por rol (ver providerWorkManagement en el BE).
  if (allowWorkDocumentManagers && user.canManageWorkDocuments) {
    return children;
  }

  const isBlocked = (blockedRoles as readonly UserRole[]).some(role => hasRole(role));

  if (isBlocked) {
    const target = redirectTo || getDefaultRouteForRole(user.role);
    return <Navigate to={target} replace />;
  }

  return children;
};

export default RestrictedRoute;
